// Static evaluation from gold's point of view. Positive is good for gold.

import {
  AGGRO,
  BLAST_NEIGHBOR_RATIO,
  EXTRA_LIFE_RATIO,
  PAWN_ADVANCE_BONUS,
  ROLE_VALUE,
  ROYAL_BONUS,
  STOPPED_PENALTY_RATIO,
} from '../constants';
import { effectiveAbilities } from './abilities';
import { isSide } from './pieces';
import { fileOf, neighborSquares, rankOf, ranksAdvanced } from './squares';
import type { Ability, Board, Piece, Pitch, Side } from './types';

export const ABILITY_VALUE: Record<Ability, number> = {
  pawn: 100,
  knight: 300,
  bishop: 320,
  rook: 500,
  king: 150,
  hop: 220,
  cannon: 380,
};

const EGG_VALUE = 180;
const EXPLODING_RATIO = 0.3;
const HUNGRY_RATIO = 0.9;

/** Material worth of a piece, before position. */
export const pieceValue = (piece: Piece): number => {
  if (piece.dormantTurns > 0) return EGG_VALUE;
  let base = effectiveAbilities(piece).reduce((sum, ability) => sum + ABILITY_VALUE[ability], 0);
  if (piece.explodeIn > 0) base *= EXPLODING_RATIO;
  if (piece.mustCapture) base *= HUNGRY_RATIO;
  const royal = piece.royal ? ROYAL_BONUS : 0;
  const lives = (piece.lives - 1) * EXTRA_LIFE_RATIO * Math.max(base, ABILITY_VALUE.pawn);
  return base + royal + lives + roleValue(piece);
};

/** What the roles of the piece are worth: the caster it carries, the energy it holds, the pitch it gives. */
export const roleValue = (piece: Piece): number =>
  (piece.casterId !== null ? ROLE_VALUE.caster : 0) +
  piece.energyCap * ROLE_VALUE.energyRoom +
  piece.energy * ROLE_VALUE.energyPoint +
  (piece.pitch !== null ? ROLE_VALUE.pitch : 0);

/** What a piece that cannot move loses of its value. */
const stopPenalty = (piece: Piece): number =>
  piece.stoppedTurns > 0 || piece.heldBy ? pieceValue(piece) * STOPPED_PENALTY_RATIO : 0;

const positionalValue = (piece: Piece, square: number, side: Side): number => {
  let value = 0;
  if (piece.abilities.includes('pawn')) value += ranksAdvanced(square, side) * PAWN_ADVANCE_BONUS;
  return value - stopPenalty(piece);
};

/**
 * The part of the score of the side that comes from pieces that cannot move:
 * the penalty on the enemy pieces, less the penalty on its own. A stop ends,
 * so the plan of a cast takes this part out of a score.
 */
export const stopShareFor = (board: Board, side: Side): number =>
  board.reduce((sum, piece) => {
    if (!piece || !isSide(piece.owner)) return sum;
    return sum + (piece.owner === side ? -1 : 1) * stopPenalty(piece);
  }, 0);

/** Pieces beside a lit explodeIn, of any owner, are as good as half gone. */
const blastPenalty = (board: Board): number => {
  let score = 0;
  board.forEach((piece, square) => {
    if (!piece || piece.explodeIn === 0) return;
    for (const neighbor of neighborSquares(square)) {
      const bystander = board[neighbor];
      if (!bystander || !isSide(bystander.owner)) continue;
      const loss = pieceValue(bystander) * BLAST_NEIGHBOR_RATIO;
      score += bystander.owner === 'gold' ? -loss : loss;
    }
  });
  return score;
};

/**
 * A caster goes to a spare pawn when its piece is gone, so while a side has
 * one, the piece that carries a caster is worth `casterWithSpare` for it and
 * not the full `caster`. The search does not see the move of the caster, so
 * this takes the difference off. Gold is positive.
 */
const spareCasterRelief = (board: Board): number => {
  const relief = ROLE_VALUE.caster - ROLE_VALUE.casterWithSpare;
  let score = 0;
  for (const side of ['gold', 'violet'] as const) {
    let carriers = 0;
    let spare = false;
    for (const piece of board) {
      if (piece?.owner !== side) continue;
      if (piece.casterId !== null) carriers += 1;
      else if (piece.kind === 'pawn') spare = true;
    }
    if (spare) score += (side === 'gold' ? -1 : 1) * carriers * relief;
  }
  return score;
};

export const evaluateBoard = (board: Board): number => {
  let score = 0;
  board.forEach((piece, square) => {
    if (!piece || !isSide(piece.owner)) return;
    const value = pieceValue(piece) + positionalValue(piece, square, piece.owner);
    score += piece.owner === 'gold' ? value : -value;
  });
  return score + blastPenalty(board) + spareCasterRelief(board);
};

export const evaluateFor = (board: Board, side: Side): number => {
  const score = evaluateBoard(board);
  return side === 'gold' ? score : -score;
};

const chebyshev = (a: number, b: number): number =>
  Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));

/**
 * The board as the aggro pitch of `side` sees it. It is the usual score with
 * three changes: the pieces of the side count for less, so a trade and a
 * risk cost it little. Each rank a piece of the side has gone forward counts.
 * Each piece of the side near an enemy royal piece counts.
 */
export const evaluateAggro = (board: Board, side: Side): number => {
  const enemyRoyals: number[] = [];
  board.forEach((piece, square) => {
    if (piece && isSide(piece.owner) && piece.owner !== side && piece.royal)
      enemyRoyals.push(square);
  });
  let score = 0;
  board.forEach((piece, square) => {
    if (!piece || !isSide(piece.owner)) return;
    const value = pieceValue(piece) + positionalValue(piece, square, piece.owner);
    if (piece.owner !== side) {
      score -= value;
      return;
    }
    score += value * AGGRO.ownWeight + ranksAdvanced(square, side) * AGGRO.advance;
    if (enemyRoyals.some((royal) => chebyshev(square, royal) <= AGGRO.pressureReach)) {
      score += AGGRO.pressure;
    }
  });
  const shared = blastPenalty(board) + spareCasterRelief(board);
  return score + (side === 'gold' ? shared : -shared);
};

/**
 * The score of a board for the side to move at a node of a search. `root` is
 * the side that asked for the move. A careful search uses the usual score.
 * An aggro search uses the score of the aggro pitch of the root, and the
 * other side plays against that.
 */
export const evaluateStyled = (board: Board, side: Side, style: Pitch, root: Side): number => {
  if (style === 'careful') return evaluateFor(board, side);
  const forRoot = evaluateAggro(board, root);
  return side === root ? forRoot : -forRoot;
};
